import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../config/axios';
import { useLanguage } from '../../context/LanguageContext';
import { 
  MdSearch, 
  MdClose, 
  MdRefresh,
  MdBusinessCenter,
  MdInfo,
  MdChevronRight,
  MdExpandMore,
  MdExpandLess
} from 'react-icons/md';
import { FaFilePdf, FaFileExcel, FaFilter, FaCalendarAlt, FaUser, FaFileContract, FaBook, FaClock } from 'react-icons/fa';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const ProjectReportPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState('');
  const [reportGenerated, setReportGenerated] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    agent_id: '',
    project_status: ''
  });
  
  // Report data
  const [reportData, setReportData] = useState([]);
  
  // Pagination states
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  // Agent detaylarını aç/kapa durumu (varsayılan: açık)
  const [expandedAgents, setExpandedAgents] = useState({});

  const toggleAgentExpand = (agentId) => {
    setExpandedAgents(prev => ({
      ...prev,
      [agentId]: prev[agentId] === false ? true : false
    }));
  };
  

  useEffect(() => {
    fetchAgents();
    fetchProjects();
  }, []);

  // Agentları getir
  const fetchAgents = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/agents', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAgents(response.data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
      setAgents([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Projeleri getir
  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    }
  };

  // Rapor oluştur
  const generateReport = async () => {
    try {
      setIsLoading(true);
      
      // API'den projeleri getir
      const projectsResponse = await api.get('/projects', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // API'den gelen veriyi kontrol et
      let projects = [];
      if (projectsResponse.data && projectsResponse.data.projects) {
        // Eğer data.projects varsa onu kullan
        projects = Array.isArray(projectsResponse.data.projects) ? projectsResponse.data.projects : [];
      } else if (Array.isArray(projectsResponse.data)) {
        // Eğer data direkt array ise onu kullan
        projects = projectsResponse.data;
      }
      
      let filteredProjects = projects.filter(project => {
        // Proje durumu filtresi
        if (filters.project_status && filters.project_status !== 'all') {
          if (String(project.project_status) !== String(filters.project_status)) return false;
        }
        
        return true;
      });

      // Tüm agentları al
      const agentsResponse = await api.get('/agents', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // API'den gelen veriyi kontrol et
      const agents = Array.isArray(agentsResponse.data) ? agentsResponse.data : [];
      
      // Agent filtresi uygula
      let filteredAgents = agents;
      if (filters.agent_id && filters.agent_id !== 'all') {
        filteredAgents = agents.filter(agent => 
          String(agent.id) === String(filters.agent_id)
        );
      }
      
      // Gerçek proje üyeleri verilerini al
      const enrichedData = [];
      
      // Eğer agent filtresi yoksa, tüm agentları göster
      if (!filters.agent_id || filters.agent_id === 'all') {
        for (const agent of agents) {
          const agentProjects = [];
          
          // Her proje için o agent'ın üye olup olmadığını kontrol et
          for (const project of filteredProjects) {
            try {
              // Proje üyelerini al
              const membersResponse = await api.get(`/projects/${project.id}/members`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
              });
              
              // Bu agent'ın bu projede üye olup olmadığını kontrol et
              // API'den gelen veri yapısına göre agent_id yerine project_member_name ile eşleştirme yap
              const member = membersResponse.data.find(m => 
                String(m.project_member_name) === String(agent.full_name) ||
                String(m.agent_id) === String(agent.id)
              );
              
              if (member) {
                agentProjects.push({
                  project_id: project.id,
                  project_name: project.project_name,
                  project_status: project.project_status,
                  project_role: member.project_role || 'Üye',
                  assigned_hours: member.assigned_hours || 0,
                  project: project
                });
              }
            } catch (error) {
              // Hata durumunda varsayılan değerlerle ekle
              agentProjects.push({
                project_id: project.id,
                project_name: project.project_name,
                project_status: project.project_status,
                project_role: 'Üye',
                assigned_hours: 0,
                project: project
              });
            }
          }
          
          // Sadece projeleri olan agentları ekle
          if (agentProjects.length > 0) {
            enrichedData.push({
              agent_id: agent.id,
              agent_name: agent.full_name,
              agent_email: agent.agent_email,
              agent: agent,
              projects: agentProjects,
              total_projects: agentProjects.length,
              total_hours: agentProjects.reduce((sum, p) => sum + (p.assigned_hours || 0), 0)
            });
          }
        }
      } else {
        // Sadece seçilen agent için
        const selectedAgent = agents.find(agent => String(agent.id) === String(filters.agent_id));
        if (selectedAgent) {
          const agentProjects = [];
          
          // Her proje için o agent'ın üye olup olmadığını kontrol et
          for (const project of filteredProjects) {
            try {
              // Proje üyelerini al
              const membersResponse = await api.get(`/projects/${project.id}/members`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
              });
              
              // Bu agent'ın bu projede üye olup olmadığını kontrol et
              // API'den gelen veri yapısına göre agent_id yerine project_member_name ile eşleştirme yap
              const member = membersResponse.data.find(m => 
                String(m.project_member_name) === String(selectedAgent.full_name) ||
                String(m.agent_id) === String(selectedAgent.id)
              );
              
              if (member) {
                agentProjects.push({
                  project_id: project.id,
                  project_name: project.project_name,
                  project_status: project.project_status,
                  project_role: member.project_role || 'Üye',
                  assigned_hours: member.assigned_hours || 0,
                  project: project
                });
              }
            } catch (error) {
              // Hata durumunda sessizce devam et
            }
          }
          
          enrichedData.push({
            agent_id: selectedAgent.id,
            agent_name: selectedAgent.full_name,
            agent_email: selectedAgent.agent_email,
            agent: selectedAgent,
            projects: agentProjects,
            total_projects: agentProjects.length,
            total_hours: agentProjects.reduce((sum, p) => sum + (p.assigned_hours || 0), 0)
          });
        }
      }
      
      setReportData(enrichedData);
      setReportGenerated(true);
      setCurrentPage(1); // Yeni rapor oluşturulduğunda ilk sayfaya dön
      
    } catch (error) {
      console.error('Error generating report:', error);
      alert(t('reports.projectReportGenerationError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Excel export
  const exportToExcel = () => {
    if (reportData.length === 0) {
      alert(t('reports.noDataToExport'));
      return;
    }

    // Helper function for clean text
    const cleanTextForExcel = (text) => {
      if (!text) return '';
      return text.toString().replace(/undefined/g, '');
    };

    // Prepare data for Excel - Agent gruplu format
    const excelData = [];
    let rowIndex = 0;
    
    reportData.forEach((agentData, agentIndex) => {
      // Agent başlığı
      excelData.push({
        [t('reports.agentName')]: cleanTextForExcel(agentData.agent_name),
        [t('reports.agentEmail')]: cleanTextForExcel(agentData.agent_email),
        [t('reports.totalProjects')]: agentData.total_projects,
        [t('reports.totalHours')]: agentData.total_hours,
        [t('reports.projectName')]: '',
        [t('reports.projectStatus')]: '',
        [t('reports.projectRole')]: '',
        [t('reports.assignedHours')]: '',
        [t('reports.projectStartDate')]: '',
        [t('reports.projectEndDate')]: ''
      });
      rowIndex++;
      
      // Projeler
      if (agentData.projects && agentData.projects.length > 0) {
        agentData.projects.forEach((project, projectIndex) => {
          excelData.push({
            [t('reports.agentName')]: '',
            [t('reports.agentEmail')]: '',
            [t('reports.totalProjects')]: '',
            [t('reports.totalHours')]: '',
            [t('reports.projectName')]: cleanTextForExcel(project.project_name),
            [t('reports.projectStatus')]: cleanTextForExcel(project.project_status),
            [t('reports.projectRole')]: cleanTextForExcel(project.project_role),
            [t('reports.assignedHours')]: cleanTextForExcel(project.assigned_hours),
            [t('reports.projectStartDate')]: cleanTextForExcel(project.project?.project_start_date),
            [t('reports.projectEndDate')]: cleanTextForExcel(project.project?.project_end_date)
          });
          rowIndex++;
        });
      } else {
        // Proje yoksa boş satır
        excelData.push({
          [t('reports.agentName')]: '',
          [t('reports.agentEmail')]: '',
          [t('reports.totalProjects')]: '',
          [t('reports.totalHours')]: '',
          [t('reports.projectName')]: t('reports.noProjectsAssigned'),
          [t('reports.projectStatus')]: '',
          [t('reports.projectRole')]: '',
          [t('reports.assignedHours')]: '',
          [t('reports.projectStartDate')]: '',
          [t('reports.projectEndDate')]: ''
        });
        rowIndex++;
      }
      
      // Agent'lar arası boşluk
      if (agentIndex < reportData.length - 1) {
        excelData.push({
          [t('reports.agentName')]: '',
          [t('reports.agentEmail')]: '',
          [t('reports.totalProjects')]: '',
          [t('reports.totalHours')]: '',
          [t('reports.projectName')]: '',
          [t('reports.projectStatus')]: '',
          [t('reports.projectRole')]: '',
          [t('reports.assignedHours')]: '',
          [t('reports.projectStartDate')]: '',
          [t('reports.projectEndDate')]: ''
        });
        rowIndex++;
      }
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    const colWidths = [
      { wch: 25 },  // Agent Adı
      { wch: 30 },  // Agent E-posta
      { wch: 15 },  // Toplam Proje
      { wch: 15 },  // Toplam Saat
      { wch: 30 },  // Proje Adı
      { wch: 15 },  // Proje Durumu
      { wch: 20 },  // Proje Rolü
      { wch: 15 },  // Atanan Saatler
      { wch: 20 },  // Başlangıç Tarihi
      { wch: 20 }   // Bitiş Tarihi
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, t('reports.projectReport'));

    // Generate filename with current date
    const currentDate = new Date().toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR').replace(/\./g, '-');
    const filename = `${t('reports.projectReport').replace(/\s+/g, '_')}_${currentDate}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  // PDF export  
  const exportToPDF = () => {
    if (reportData.length === 0) {
      alert(t('reports.noDataToExport'));
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation

    // Helper function to handle Turkish characters
    const cleanText = (text) => {
      if (!text) return '';
      
      // Convert Turkish characters to Latin equivalents for PDF compatibility
      const turkishToLatin = {
        'ç': 'c', 'Ç': 'C',
        'ğ': 'g', 'Ğ': 'G', 
        'ı': 'i', 'I': 'I',
        'İ': 'I', 'i': 'i',
        'ö': 'o', 'Ö': 'O',
        'ş': 's', 'Ş': 'S',
        'ü': 'u', 'Ü': 'U'
      };
      
      let cleanedText = text.toString().replace(/undefined/g, '');
      
      // Replace Turkish characters
      for (const [turkish, latin] of Object.entries(turkishToLatin)) {
        cleanedText = cleanedText.replace(new RegExp(turkish, 'g'), latin);
      }
      
      return cleanedText;
    };

    // Add title
    doc.setFontSize(16);
    doc.text(t('reports.projectReport'), 14, 22);

    // Add generation date
    doc.setFontSize(10);
    doc.text(`${t('reports.reportDate')}: ${new Date().toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR')}`, 14, 30);

    // Prepare table data with clean text - Agent gruplu format
    let currentY = 40;
    
    reportData.forEach((agentData, agentIndex) => {
      // Agent başlığı
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(cleanText(agentData.agent_name), 14, currentY);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`${t('reports.agentEmail')}: ${cleanText(agentData.agent_email)}`, 14, currentY + 6);
      doc.text(`${t('reports.totalProjects')}: ${agentData.total_projects} | ${t('reports.totalHours')}: ${agentData.total_hours}`, 14, currentY + 12);
      
      currentY += 20;
      
      // Projeler tablosu
      if (agentData.projects && agentData.projects.length > 0) {
        const tableData = agentData.projects.map((project) => [
          cleanText(project.project_name || '').substring(0, 30),
          cleanText(project.project_status || ''),
          cleanText(project.project_role || ''),
          cleanText(project.assigned_hours || '0'),
          cleanText(project.project?.project_start_date || ''),
          cleanText(project.project?.project_end_date || '')
        ]);

        autoTable(doc, {
          head: [[t('reports.projectName'), t('reports.projectStatus'), t('reports.projectRole'), t('reports.assignedHours'), t('reports.projectStartDate'), t('reports.projectEndDate')]],
          body: tableData,
          startY: currentY,
          styles: {
            fontSize: 8,
            cellPadding: 2,
            font: 'helvetica',
            fontStyle: 'normal'
          },
          headStyles: {
            fillColor: [66, 139, 202],
            textColor: 255,
            fontSize: 9,
            fontStyle: 'bold',
            font: 'helvetica'
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245]
          },
          columnStyles: {
            0: { cellWidth: 50 },  // Proje Adı
            1: { cellWidth: 25 },  // Proje Durumu
            2: { cellWidth: 30 },  // Proje Rolü
            3: { cellWidth: 20 },  // Atanan Saatler
            4: { cellWidth: 25 },  // Başlangıç Tarihi
            5: { cellWidth: 25 }   // Bitiş Tarihi
          },
          margin: { left: 10, right: 10 },
          tableWidth: '100%'
        });
        
        currentY = doc.lastAutoTable.finalY + 15;
      } else {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'italic');
        doc.text(t('reports.noProjectsAssigned'), 14, currentY);
        currentY += 15;
      }
      
      // Agent'lar arası boşluk
      if (agentIndex < reportData.length - 1) {
        currentY += 10;
      }
    });

    // Generate filename with current date
    const currentDate = new Date().toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR').replace(/\./g, '-');
    const filename = `${t('reports.projectReport').replace(/\s+/g, '_')}_${currentDate}.pdf`;

    // Save file
    doc.save(filename);
  };

  // Filtreleri temizle
  const clearFilters = () => {
    setFilters({
      agent_id: '',
      project_status: ''
    });
    setReportData([]);
    setReportGenerated(false);
    setCurrentPage(1);
  };

  // Pagination logic
  const pageSizeOptions = [10, 20, 50, 100];
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = reportData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(reportData.length / itemsPerPage);
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(Number(newSize));
    setCurrentPage(1); // Sayfa boyutu değiştiğinde ilk sayfaya dön
  };

  return (
    <div className="h-full overflow-auto">
      {/* Breadcrumb */}
      <div className="flex justify-between items-center mb-6">
        <nav className="flex items-center text-sm">
          <div className="flex items-center bg-[#232b3b] px-4 py-2 rounded-md shadow-sm">
            <span className="text-[#fff] font-medium">
              {t('common.reports')}
            </span>
            <MdChevronRight className="mx-2 text-[#fff]" size={20} />
            <span className="text-[#fff] font-medium">
              {t('reports.projectReport')}
            </span>
          </div>
        </nav>
      </div>

      {/* Filter Panel */}
      <div className="dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 mb-6">
        {/* Filter Panel Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
          <h1 className="text-lg font-medium text-gray-800 dark:text-white">{t('reports.projectInfo')}</h1>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
            {/* Agent Seçimi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('reports.agentSelection')}
              </label>
              <select
                value={filters.agent_id}
                onChange={(e) => setFilters(prev => ({ ...prev, agent_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('reports.selectAgent')}</option>
                <option value="all">{t('reports.all')}</option>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.full_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Proje Durumu Seçimi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('reports.projectStatus')}
              </label>
              <select
                value={filters.project_status}
                onChange={(e) => setFilters(prev => ({ ...prev, project_status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('reports.selectProjectStatus')}</option>
                <option value="all">{t('reports.all')}</option>
                <option value="planning">{t('reports.planning')}</option>
                <option value="inProgress">{t('reports.inProgress')}</option>
                <option value="completed">{t('reports.completed')}</option>
                <option value="cancelled">{t('reports.cancelled')}</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-4">
            <button
              onClick={generateReport}
              disabled={isLoading}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-[#232b3b] text-white rounded-md hover:bg-[#1e2230] disabled:opacity-50"
            >
              <MdSearch size={16} />
              {isLoading ? t('reports.generating') : t('common.show')}
            </button>

            <button
              onClick={clearFilters}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
            >
              <MdClose size={16} />
              {t('reports.clearFilters')}
            </button>

            <button
              onClick={exportToExcel}
              disabled={!reportGenerated || reportData.length === 0}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:hover:bg-gray-400 disabled:cursor-not-allowed"
            >
              <FaFileExcel size={16} />
              {t('reports.downloadExcel')}
            </button>

            <button
              onClick={exportToPDF}
              disabled={!reportGenerated || reportData.length === 0}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:hover:bg-gray-400 disabled:cursor-not-allowed"
            >
              <FaFilePdf size={16} />
              {t('reports.downloadPDF')}
            </button>
          </div>
        </div>
      </div>

      {/* Report Results */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Table Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
          <h1 className="text-lg font-medium text-gray-800 dark:text-white">{t('reports.report')}</h1>
        </div>
        
        {reportGenerated ? (
          <>
            {/* Sayfa boyutu seçici */}
            <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex justify-between items-center">
              <div className="flex items-center gap-2">
                 <span className="text-sm text-gray-600 dark:text-gray-300">{t('common.show')}</span>
                 <select
                   value={itemsPerPage}
                   onChange={(e) => handlePageSizeChange(e.target.value)}
                   className="border border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                 >
                   {pageSizeOptions.map(size => (
                     <option key={size} value={size}>
                       {size}
                     </option>
                   ))}
                 </select>
                 <span className="text-sm text-gray-600 dark:text-gray-300">{t('table.records')}</span>
               </div>
               <div className="flex items-center gap-4">
                 <span className="text-sm text-gray-600 dark:text-gray-300">
                   {t('table.totalRecordsFound', { count: reportData.length })}
                 </span>
               </div>
             </div>

             {reportData.length > 0 ? (
            <>
              {/* Desktop Table - Agent Gruplu Görünüm */}
              <div className="hidden lg:block overflow-x-auto">
                <div className="space-y-0">
                  {currentData.map((agentData, index) => (
                    <div
                      key={index}
                      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden ${index === currentData.length - 1 ? 'rounded-b-lg' : 'rounded-b-none'}`}
                    >
                      {/* Agent Header */}
                      <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                        <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div>
                              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                {agentData.agent_name}
                              </h3>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {agentData.agent_email}
                              </p>
                            </div>
                          </div>
                        <div className="flex items-center space-x-4">
                          <button
                            type="button"
                            onClick={() => toggleAgentExpand(agentData.agent_id)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                            aria-label={expandedAgents[agentData.agent_id] === false ? t('common.open') : t('common.close')}
                          >
                            {expandedAgents[agentData.agent_id] === false ? (
                              <MdExpandMore size={18} />
                            ) : (
                              <MdExpandLess size={18} />
                            )}
                          </button>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                {agentData.total_projects}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {t('reports.totalProjects')}
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                                {agentData.total_hours}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {t('reports.totalHours')}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Projects Table */}
                      {expandedAgents[agentData.agent_id] === false ? null : (
                      <div className="px-0 pb-0">
                        {agentData.projects.length > 0 ? (
                           <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border-l-0 border-r-0">
                               <thead className="bg-gray-50 dark:bg-gray-700">
                                 <tr>
                                   <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                     {t('reports.projectName')}
                                   </th>
                                   <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                     {t('reports.projectStatus')}
                                   </th>
                                   <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                     {t('reports.projectRole')}
                                   </th>
                                   <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                     {t('reports.assignedHours')}
                                   </th>
                                   <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                     {t('reports.projectStartDate')}
                                   </th>
                                   <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                     {t('reports.projectEndDate')}
                                   </th>
                                 </tr>
                               </thead>
                               <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                 {agentData.projects.map((project, projectIndex) => (
                                   <tr key={projectIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                     <td className="px-4 py-3 whitespace-nowrap">
                                       <div className="text-sm font-medium text-gray-900 dark:text-white">
                                         {project.project_name}
                                       </div>
                                     </td>
                                     <td className="px-4 py-3 whitespace-nowrap">
                                       <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                         project.project_status === 'planning' 
                                           ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                           : project.project_status === 'inProgress'
                                           ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                           : project.project_status === 'completed'
                                           ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                           : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                       }`}>
                                         {project.project_status}
                                       </span>
                                     </td>
                                     <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                       {project.project_role}
                                     </td>
                                     <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600 dark:text-blue-400">
                                       {project.assigned_hours} {t('reports.hours')}
                                     </td>
                                     <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                       {project.project?.project_start_date || '-'}
                                     </td>
                                     <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                       {project.project?.project_end_date || '-'}
                                     </td>
                                   </tr>
                                 ))}
                               </tbody>
                            </table>
                           </div>
                         ) : (
                           <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                             <p>{t('reports.noProjectsAssigned')}</p>
                           </div>
                         )}
                      </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile Cards - Agent Gruplu Görünüm */}
              <div className="lg:hidden space-y-0 p-4">
                {currentData.map((agentData, index) => (
                  <div
                    key={index}
                    className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden ${index === currentData.length - 1 ? 'rounded-b-lg' : 'rounded-b-none'}`}
                  >
                    {/* Agent Header */}
                    <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white">
                            {agentData.agent_name}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {agentData.agent_email}
                          </p>
                        </div>
                        <div className="flex space-x-3">
                          <button
                            type="button"
                            onClick={() => toggleAgentExpand(agentData.agent_id)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                            aria-label={expandedAgents[agentData.agent_id] === false ? t('common.open') : t('common.close')}
                          >
                            {expandedAgents[agentData.agent_id] === false ? (
                              <MdExpandMore size={18} />
                            ) : (
                              <MdExpandLess size={18} />
                            )}
                          </button>
                          <div className="text-center">
                            <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                              {agentData.total_projects}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {t('reports.projects')}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-green-600 dark:text-green-400">
                              {agentData.total_hours}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {t('reports.hours')}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Projects Grid - Mobile */}
                    {expandedAgents[agentData.agent_id] === false ? null : (
                    <div className="px-0 pb-0">
                      {agentData.projects.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3">
                          {agentData.projects.map((project, projectIndex) => (
                            <div key={projectIndex} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
                              {/* Proje Adı */}
                              <div className="mb-2">
                                <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {project.project_name}
                                </h4>
                              </div>
                              
                              {/* Proje Durumu */}
                              <div className="mb-2">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  project.project_status === 'planning' 
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                    : project.project_status === 'inProgress'
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                    : project.project_status === 'completed'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                }`}>
                                  {project.project_status}
                                </span>
                              </div>
                              
                              {/* Proje Detayları */}
                              <div className="space-y-1 text-xs">
                                <div className="flex justify-between">
                                  <span className="text-gray-500 dark:text-gray-400">{t('reports.projectRole')}:</span>
                                  <span className="text-gray-900 dark:text-white">{project.project_role}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500 dark:text-gray-400">{t('reports.assignedHours')}:</span>
                                  <span className="font-medium text-blue-600 dark:text-blue-400">
                                    {project.assigned_hours}h
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500 dark:text-gray-400">{t('reports.projectStartDate')}:</span>
                                  <span className="text-gray-900 dark:text-white">
                                    {project.project?.project_start_date || '-'}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500 dark:text-gray-400">{t('reports.projectEndDate')}:</span>
                                  <span className="text-gray-900 dark:text-white">
                                    {project.project?.project_end_date || '-'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                       ) : (
                         <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                           <p className="text-sm">{t('reports.noProjectsAssigned')}</p>
                         </div>
                       )}
                    </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Sayfalama */}
              {totalPages > 1 && (
                <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6">
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between mb-6">
                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {t('table.showing')}{' '}
                        <span className="font-medium">{indexOfFirstItem + 1}</span>
                        {' '}-{' '}
                        <span className="font-medium">
                          {Math.min(indexOfLastItem, reportData.length)}
                        </span>{' '}
                        / {t('table.total')}{' '}
                        <span className="font-medium">{reportData.length}</span>{' '}
                        {t('table.records')}
                      </p>
                    </div>
                    <div>
                      <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium ${
                            currentPage === 1
                              ? 'bg-gray-100 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                        >
                          {t('table.previous')}
                        </button>
                        {[...Array(totalPages)].map((_, index) => (
                          <button
                            key={index + 1}
                            onClick={() => handlePageChange(index + 1)}
                            className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                              currentPage === index + 1
                                ? 'bg-blue-600 text-white'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            {index + 1}
                          </button>
                        ))}
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium ${
                            currentPage === totalPages
                              ? 'bg-gray-100 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                        >
                          {t('table.next')}
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
              ) : (
                <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                  <p>{t('reports.noDataFound')}</p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-10 text-gray-500 dark:text-gray-400">
              <p>{t('reports.useFiltersToGenerate')}</p>
            </div>
          )}
        </div>

    </div>
  );
};

export default ProjectReportPage;
